/**
 * Deeper editorial profiles for priority state-law pages.
 *
 * These summaries are educational, deliberately narrow, and tied to free
 * primary-law sources. They do not predict whether a particular claim wins.
 */

import { MIDWEST_STATE_LAW_PROFILES } from "@/data/state-law-profiles/midwest";
import { NORTHEAST_STATE_LAW_PROFILES } from "@/data/state-law-profiles/northeast";
import { SOUTH_STATE_LAW_PROFILES } from "@/data/state-law-profiles/south";
import { WEST_STATE_LAW_PROFILES } from "@/data/state-law-profiles/west";


export interface StateLawSource {
  label: string;
  citation: string;
  url: string;
}

export interface StateLawElement {
  name: string;
  explanation: string;
}

export interface StateLawCaseExample {
  name: string;
  citation: string;
  facts: string;
  outcome: string;
  lesson: string;
  sourceUrl: string;
}

export interface EnhancedStateLawProfile {
  /** USPS two-letter code. Every state carries a profile. */
  stateCode: string;
  distinctiveRule: string;
  overview: string;
  burden: string;
  elements: StateLawElement[];
  pitfalls: string[];
  workedExample: {
    title: string;
    facts: string;
    analysis: string;
    nextStep: string;
  };
  recordingGuidance: string;
  caseExample: StateLawCaseExample;
  sources: StateLawSource[];
}

const CORE_STATE_LAW_PROFILES: EnhancedStateLawProfile[] = [
  {
    stateCode: "TX",
    distinctiveRule:
      "Texas focuses on whether the use visibly asserted a right inconsistent with the owner's title; neighborly accommodation is not enough.",
    overview:
      "Texas prescriptive-easement doctrine borrows the ten-year limitations period used for adverse possession, but the claimant seeks only a defined use, not ownership. The evidence must identify the route and show open, notorious, continuous, exclusive, and adverse use under a claim of right for the full period.",
    burden:
      "The person claiming the easement must prove every element. Texas decisions apply the ten-year framework strictly, and unexplained use that looks like a revocable neighborly favor may not establish hostility or notice.",
    elements: [
      {
        name: "A visible, definite route",
        explanation:
          "The driveway, gate, lane, or other use must be apparent enough to notify the owner, and the claimed corridor must be identifiable rather than shifting across the tract.",
      },
      {
        name: "Continuous use for ten years",
        explanation:
          "The use must continue in the manner ordinary for that kind of easement throughout the limitations period; a road need not be driven every day, but sporadic convenience is weaker evidence.",
      },
      {
        name: "Adverse claim of right",
        explanation:
          "The conduct must be inconsistent with the owner's right to exclude. Subjective hostility is not the point; outward acts must communicate that the claimant is using the route as a right, not by permission.",
      },
      {
        name: "Use independent of the public",
        explanation:
          "For a private easement, the claimant must connect the asserted right to the benefited land instead of relying only on the same casual use made by neighbors or the general public.",
      },
    ],
    pitfalls: [
      "A license, handshake permission, gate key given as a favor, or other consent defeats adversity while that permission continues.",
      "Evidence that many neighbors used the same road without asserting an individual right can fail to prove a private prescriptive easement.",
      "Changing the route or allowing a substantial interruption can keep the claimant from proving one definite, continuous ten-year use.",
      "Landlocked status does not by itself prove prescription; an easement by necessity has different elements tied to prior common ownership and necessity when the tract was severed.",
    ],
    workedExample: {
      title: "A ranch gate used with the owner's consent",
      facts:
        "A homeowner has crossed a neighbor's ranch road for twelve years. The neighbor supplied a gate key when the use began and occasionally asked the homeowner to close the gate after cattle moved.",
      analysis:
        "Twelve years clears the calendar threshold, but the original permission is the central problem. Continued use can look like the same revocable accommodation unless later conduct clearly communicated an adverse claim and then continued for a new ten-year period.",
      nextStep:
        "Collect the earliest messages, gate agreements, surveys, photographs, and statements from prior owners before treating the road as a permanent property right.",
    },
    recordingGuidance:
      "A negotiated easement should state the benefited and burdened tracts, legal description, permitted uses, width, maintenance duties, and termination terms, then be recorded with the county clerk where the land lies. Confirm the clerk's current local schedule because statutory base charges and county records-preservation charges can combine.",
    caseExample: {
      name: "Othen v. Rosier",
      citation: "226 S.W.2d 622 (Tex. 1950)",
      facts:
        "A landowner used a roadway across neighboring tracts to reach a public road and sued after a levee made the route impassable, asserting both necessity and prescription.",
      outcome:
        "The Texas Supreme Court rejected the claimed easement. The proof did not establish the required necessity at the time the commonly owned land was severed, and the road use was not shown to be adverse rather than permissive for the prescriptive period.",
      lesson:
        "Long use and present-day landlocked conditions do not substitute for proof of each doctrine's separate elements.",
      sourceUrl: "https://hallapproved.com/tx/cases/supreme/1950/1725319/",
    },
    sources: [
      {
        label: "Texas adverse-possession definitions and ten-year period",
        citation: "Tex. Civ. Prac. & Rem. Code §§ 16.021, 16.026",
        url: "https://statutes.capitol.texas.gov/Docs/CP/htm/CP.16.htm",
      },
      {
        label: "Texas recording-fee authority",
        citation: "Tex. Local Gov't Code §§ 118.011, 118.013",
        url: "https://statutes.capitol.texas.gov/docs/lg/pdf/lg.118.pdf",
      },
    ],
  },
  {
    stateCode: "FL",
    distinctiveRule:
      "Florida's twenty-year private prescriptive-easement rule is not the four-year maintained-road dedication rule in section 95.361.",
    overview:
      "Florida private prescriptive easements remain a common-law doctrine. A claimant must prove a particular, actual use of another's land that was open, adverse, continuous, and uninterrupted for twenty years, with actual or imputed knowledge by the owner. Section 95.361 addresses presumed dedication of roads constructed and maintained by a government entity; it is not a shortcut for a neighbor's driveway claim.",
    burden:
      "The claimant carries the burden and Florida courts describe the required proof as clear and positive. The location and extent of the claimed use matter: vague testimony about people crossing somewhere on a tract is not a substitute for a defined route.",
    elements: [
      {
        name: "Particular and actual use",
        explanation:
          "The claimant must identify what was used and for what purpose, such as one driveway for residential access rather than an undefined right over the whole parcel.",
      },
      {
        name: "Open use with owner knowledge",
        explanation:
          "The use must be visible and either known to the owner or sufficiently apparent that knowledge is attributed to the owner.",
      },
      {
        name: "Adverse use under a claim of right",
        explanation:
          "The use cannot depend on permission, indulgence, or a temporary license. The conduct must objectively assert continued use despite the owner's right to exclude.",
      },
      {
        name: "Twenty continuous years",
        explanation:
          "The same legally meaningful use must persist without effective interruption for the entire common-law period.",
      },
    ],
    pitfalls: [
      "Do not count four years of neighbor use under section 95.361; that statute concerns roads constructed and maintained by a county, municipality, or the state.",
      "Permission prevents the use from being adverse, even when the permission was informal or granted by an earlier owner.",
      "An uncertain route, width, or endpoint can defeat relief because a court must be able to describe the right it is recognizing.",
      "A standard title search may not reveal an unadjudicated prescriptive claim because no written easement created it.",
    ],
    workedExample: {
      title: "A beach-access path used for eighteen years",
      facts:
        "Neighbors have openly walked the same marked path to the shore for eighteen years without asking permission, and the owner has never blocked it.",
      analysis:
        "The visible route and lack of express permission may support several elements, but eighteen years is short of Florida's twenty-year common-law period. The four-year public-road rule does not apply merely because many people used the path.",
      nextStep:
        "Build a dated history from surveys, aerials, photographs, prior-owner statements, signs, gates, and written communications before deciding whether any earlier use can lawfully be counted.",
    },
    recordingGuidance:
      "An agreed easement is recorded in the official records by the clerk of the circuit court for the county where the parcel is located. Check that clerk's current recording rules and the statewide fee authority before filing; include a recordable legal description rather than relying on a parcel-map screenshot.",
    caseExample: {
      name: "Downing v. Bird",
      citation: "100 So. 2d 57 (Fla. 1958)",
      facts:
        "A property owner challenged paving, a fence, and a building affecting a roughly fifty-foot strip. The city and another defendant relied on statutory road dedication and prescription.",
      outcome:
        "The Florida Supreme Court found the proof insufficient for those defenses. The evidence did not adequately establish the required adverse prescriptive use or the route, termini, and width, and the maintained-road requirements had not been proved.",
      lesson:
        "A court needs clear evidence of both the character of the use and the exact physical right claimed.",
      sourceUrl: "https://case-law.vlex.com/vid/downing-v-bird-891444924",
    },
    sources: [
      {
        label: "Florida's maintained-road dedication statute",
        citation: "Fla. Stat. § 95.361",
        url: "https://www.leg.state.fl.us/Statutes/index.cfm?App_mode=Display_Statute&URL=0000-0099/0095/Sections/0095.361.html",
      },
      {
        label: "Florida recording charges",
        citation: "Fla. Stat. § 28.24",
        url: "https://www.leg.state.fl.us/Statutes/index.cfm?App_mode=Display_Statute&URL=0000-0099/0028/Sections/0028.24.html",
      },
    ],
  },
  {
    stateCode: "GA",
    distinctiveRule:
      "Georgia gives qualifying private ways a specific seven-year path, but the user must keep the way open and in repair; broader prescriptive rights follow different twenty-year or written-title rules.",
    overview:
      "Georgia requires care with labels. Code section 44-9-1 addresses a private way over another's land and permits prescription after seven years of uninterrupted use when the claimant has kept the way open and in repair. Georgia's general prescription statutes use a twenty-year period without written title and seven years under written evidence of title. The claimed right must therefore be matched to the correct statutory track.",
    burden:
      "The claimant must prove the use and the statutory facts supporting the selected route to prescription. For a private way, maintenance is evidence that gives the owner notice of an adverse claim; merely traveling a road for years may not be enough.",
    elements: [
      {
        name: "A private way within the statutory scope",
        explanation:
          "The route must be a private way over another's land, not simply a claim to possess the strip or an undefined recreational use.",
      },
      {
        name: "Constant and uninterrupted use",
        explanation:
          "For the private-way statute, qualifying use must continue for seven years without legal steps that abolish or interrupt the asserted way.",
      },
      {
        name: "Open maintenance and repair",
        explanation:
          "The claimant's work must do more than remove an occasional limb; repair or upkeep should be substantial and visible enough to signal a claim of right.",
      },
      {
        name: "A route confined to the historic use",
        explanation:
          "A prescriptive private way cannot be enlarged beyond the route and manner established by the evidence, and Georgia statutes limit how wide such a way may become.",
      },
    ],
    pitfalls: [
      "Using a dirt road for seven years without keeping it open and in repair can fail the private-way test.",
      "Maintenance done only with the owner's permission does not necessarily communicate an adverse claim.",
      "The seven-year private-way rule should not be confused with the twenty-year rule for prescription without written title under Georgia's broader property statutes.",
      "A claimant cannot use years of one route to establish a materially different route or a broader use.",
    ],
    workedExample: {
      title: "A lake road cleared but never repaired",
      facts:
        "A cabin owner used an eight-foot dirt road across the next lot for nine years and occasionally moved fallen branches, but never graded, ditched, graveled, or otherwise repaired it.",
      analysis:
        "The calendar and a definite route favor the cabin owner, but Georgia's repair requirement is a separate proof problem. Incidental cleanup may not provide the owner with notice that the user is asserting a prescriptive private way.",
      nextStep:
        "Gather invoices, photographs, grading records, witness statements, and communications showing who maintained the road, when, and whether the neighboring owner consented.",
    },
    recordingGuidance:
      "A written easement or judgment affecting title is filed with the clerk of the superior court in the county where the land lies. Before filing, confirm the statewide fee authority and local indexing requirements, and attach a survey-grade description that matches the agreed route and width.",
    caseExample: {
      name: "Simmons v. Bearden",
      citation: "234 Ga. App. 81, 506 S.E.2d 202 (1998)",
      facts:
        "The parties agreed that a field road had been used for more than seven years and was no more than twenty feet wide, but the proof of upkeep was largely occasional removal of limbs and dead trees.",
      outcome:
        "The court upheld denial of the private-way claim because the evidence did not show repair or maintenance sufficient to notify the owner that the user intended to prescribe against the land.",
      lesson:
        "In Georgia, years of travel do not erase the need to prove the private way was kept open and in repair.",
      sourceUrl: "https://law.justia.com/cases/georgia/court-of-appeals/1998/a98a0849.html",
    },
    sources: [
      {
        label: "Georgia private-way prescription",
        citation: "O.C.G.A. §§ 44-9-1, 44-9-54",
        url: "https://law.justia.com/codes/georgia/title-44/chapter-9/article-3/section-44-9-54/",
      },
      {
        label: "Georgia prescription without written title",
        citation: "O.C.G.A. § 44-5-163",
        url: "https://law.justia.com/codes/georgia/title-44/chapter-5/article-7/section-44-5-163/",
      },
      {
        label: "Georgia recording-fee authority",
        citation: "O.C.G.A. § 15-6-77",
        url: "https://law.justia.com/codes/georgia/title-15/chapter-6/article-3/section-15-6-77/",
      },
    ],
  },
  {
    stateCode: "AZ",
    distinctiveRule:
      "Arizona can presume that long, open use was adverse, but use across large, unenclosed, vacant land may instead be treated as permissive.",
    overview:
      "Arizona courts borrow the ten-year period in A.R.S. section 12-526 for prescriptive easements. A claimant generally must identify a definite use that was open, visible, continuous, and unmolested for ten years. That showing can support a presumption that the use was under a claim of right, but Arizona decisions treat open-range and vacant-land settings with special caution because tolerated travel across undeveloped land may begin as permission rather than an adverse claim.",
    burden:
      "The claimant must prove the facts supporting prescription. Once qualifying open and continuous use is established, the evidentiary focus may shift to whether the owner can show permission; the physical character of the burdened land and the relationship between the parties can determine which presumption fits.",
    elements: [
      {
        name: "A definite route or use",
        explanation:
          "The evidence must identify the road, driveway, trail, or other limited use claimed rather than an undefined right to cross the parcel wherever convenient.",
      },
      {
        name: "Open and visible conduct",
        explanation:
          "Use must be apparent enough to put a reasonably attentive owner on notice, through a traveled road, grading, gates, or other observable acts.",
      },
      {
        name: "Continuous use for ten years",
        explanation:
          "The use must continue without an effective interruption for the statutory period in the manner ordinary for the claimed easement.",
      },
      {
        name: "Adverse use under claim of right",
        explanation:
          "The claimant must use the route as a right rather than through a revocable accommodation, while accounting for Arizona's fact-sensitive presumptions about permission and hostility.",
      },
    ],
    pitfalls: [
      "A gate key, oral consent, family arrangement, or other evidence of permission can defeat adversity no matter how regularly the route was used.",
      "Travel across large, unenclosed, vacant, or desert land may be presumed permissive, so duration alone can be weak proof of an adverse claim.",
      "A claimant cannot turn years of use on one visible road into a broader right over routes or purposes that the evidence does not establish.",
      "A driveway claim should not be confused with adverse possession of the land itself or an easement by necessity, which require different proof.",
    ],
    workedExample: {
      title: "An eleven-year road across an undeveloped parcel",
      facts:
        "A homeowner has used and periodically graded the same dirt road across a neighboring parcel for eleven years without requesting permission. The neighboring tract is large, unfenced, and mostly vacant.",
      analysis:
        "The visible route, maintenance, and eleven-year timeline support several prescriptive elements. The vacant, unenclosed condition creates a serious Arizona-specific issue, however: a court may view the use as tolerated and permissive unless the homeowner can point to conduct that clearly communicated a claim of right.",
      nextStep:
        "Collect dated grading records, aerial photographs, gate history, communications, and statements from prior owners, then determine whether an express recorded agreement is more reliable than litigation over the historic use.",
    },
    recordingGuidance:
      "Record a negotiated easement with the county recorder where the land is located, using a legal description that fixes the corridor and states its permitted uses and maintenance duties. Arizona generally charges a statutory flat recording fee per instrument, but confirm the recorder's current schedule and formatting rules. A claimed prescriptive right commonly needs a recorded court judgment or later written agreement before it appears as dependable record-title evidence.",
    caseExample: {
      name: "Harambasic v. Owens",
      citation: "186 Ariz. 622, 925 P.2d 39 (Ct. App. 1996)",
      facts:
        "A dispute over access to ranch property required the court to examine long use of a roadway and the significance of later conduct that the owner characterized as permission.",
      outcome:
        "The Arizona Court of Appeals explained that open, visible, continuous, and unmolested use for the prescriptive period can create a presumption that the use was adverse, leaving the owner to produce evidence that it was permissive.",
      lesson:
        "Arizona's result can turn on whether the evidence shows an existing claim of right or merely a neighbor's revocable accommodation.",
      sourceUrl: "https://hallapproved.com/az/cases/appeals/1996/1205533/",
    },
    sources: [
      {
        label: "Arizona ten-year real-property limitation",
        citation: "A.R.S. § 12-526",
        url: "https://www.azleg.gov/ars/12/00526.htm",
      },
      {
        label: "Arizona recording-fee authority",
        citation: "A.R.S. § 11-475",
        url: "https://www.azleg.gov/ars/11/00475.htm",
      },
      {
        label: "Arizona vacant-land permission analysis",
        citation: "Spaulding v. Pouliot, 218 Ariz. 196 (Ct. App. 2008)",
        url: "https://hallapproved.com/az/cases/appeals/2008/2634318/",
      },
    ],
  },
  {
    stateCode: "CO",
    distinctiveRule:
      "Colorado uses an eighteen-year period and recognizes qualifying use that is adverse or made under an attempted but ineffective grant.",
    overview:
      "Colorado prescriptive-easement decisions use the eighteen-year period in C.R.S. section 38-41-101. The claimant must show an open or notorious use that continued without effective interruption for the full period and was adverse, or occurred under an attempted grant that failed to create an enforceable written easement. Long qualifying use can support a rebuttable presumption of adversity, but evidence of permission can defeat that inference.",
    burden:
      "The claimant bears the burden of establishing the easement and its scope. Colorado's modern adverse-possession statute contains requirements directed at claims to fee title, so a prescriptive-easement analysis should not automatically import every procedural rule governing ownership claims.",
    elements: [
      {
        name: "Open or notorious use",
        explanation:
          "A road, driveway, ditch, utility use, or other asserted right must be visible enough that a reasonably attentive owner could discover it.",
      },
      {
        name: "Eighteen years without effective interruption",
        explanation:
          "The legally meaningful use must continue in its ordinary pattern for eighteen years without abandonment or an owner action that stops the claimed right.",
      },
      {
        name: "Adversity or an ineffective grant",
        explanation:
          "Colorado recognizes use under a claim of right and also addresses reliance on an attempted grant that did not successfully create an express easement.",
      },
      {
        name: "Scope measured by historic use",
        explanation:
          "The route, purpose, and intensity proved over time define the resulting right; a claimant does not receive every use that a broad written easement might have allowed.",
      },
    ],
    pitfalls: [
      "A key, license, verbal approval, or family accommodation can rebut the inference of adversity and keep the eighteen-year clock from running.",
      "Colorado law bars prescriptive acquisition against specified public lands and public rights, even when use has continued for decades.",
      "Abandonment or nonuse may affect one purpose or portion of a claimed easement without resolving every other use of the same corridor.",
      "Acknowledging that the neighbor owns the burdened land is not the same as admitting the use is permissive; title and a limited use right are different questions.",
    ],
    workedExample: {
      title: "A gated driveway used for twenty years",
      facts:
        "A homeowner has crossed the same gravel driveway for twenty years. Midway through that period, the neighbor installed a gate and supplied a keypad code without putting the arrangement in writing.",
      analysis:
        "The visible route and twenty-year history exceed Colorado's period, but the gate arrangement is ambiguous. It could document neighborly permission or simply accommodate an existing claim of access. The words and conduct surrounding the code, along with any earlier attempted easement, matter more than the hardware alone.",
      nextStep:
        "Preserve messages, surveys, gate records, maintenance receipts, and testimony from the time the code was issued before assuming the route is either licensed or prescriptive.",
    },
    recordingGuidance:
      "Record a written easement with the county clerk and recorder where the property lies, with a recordable legal description and clear terms for width, use, maintenance, and termination. Colorado offices use reception numbers for modern filings, and current fees and document requirements should be confirmed locally. A prescriptive claim generally reaches the land records through a recorded judgment or a later written settlement.",
    caseExample: {
      name: "Matoush v. Lovingood",
      citation: "159 P.3d 741 (Colo. App. 2006)",
      facts:
        "Owners disputed the continuing scope of an easement associated with an alley after some historic purposes had ceased while other uses remained.",
      outcome:
        "The Colorado Court of Appeals treated abandonment as dependent on the particular easement use and the evidence of intent, rather than assuming that loss of one purpose automatically erased every remaining right in the corridor.",
      lesson:
        "Colorado easement disputes require a purpose-by-purpose examination of scope, use, and abandonment, not just a yes-or-no label for the strip.",
      sourceUrl: "https://law.justia.com/cases/colorado/court-of-appeals/2006/05ca0538-0.html",
    },
    sources: [
      {
        label: "Colorado eighteen-year real-property limitation",
        citation: "C.R.S. § 38-41-101",
        url: "https://leg.colorado.gov/colorado-revised-statutes",
      },
      {
        label: "Colorado recording duties",
        citation: "C.R.S. § 30-10-406",
        url: "https://leg.colorado.gov/colorado-revised-statutes",
      },
      {
        label: "Colorado Supreme Court easement decision",
        citation: "Lobato v. Taylor, 71 P.3d 938 (Colo. 2002)",
        url: "https://law.justia.com/cases/colorado/supreme-court/2002/00sc527-0.html",
      },
    ],
  },
  {
    stateCode: "WA",
    distinctiveRule:
      "Washington often presumes that long, unchallenged neighborly use was permissive, so silence alone may not prove adversity.",
    overview:
      "Washington courts borrow a ten-year period from RCW 4.16.020 and require open and notorious use over a uniform route, continuous and uninterrupted use, adversity, and owner knowledge while the owner could protect the property. A central Washington distinction is the presumption that apparently neighborly use began with permission; the claimant may need affirmative conduct showing a claim of right rather than relying only on years without objection.",
    burden:
      "The claimant must prove the prescriptive elements and overcome the applicable inference of permission. Courts apply an objective test to the use itself, so private beliefs about ownership matter less than what the parties did and what those acts communicated.",
    elements: [
      {
        name: "Open and notorious use",
        explanation:
          "The route and activity must be visible enough to provide actual or constructive notice to the owner of the burdened parcel.",
      },
      {
        name: "A uniform route",
        explanation:
          "The claimant must establish a reasonably definite driveway, road, path, or other corridor rather than changing crossings across the property.",
      },
      {
        name: "Ten continuous years",
        explanation:
          "The ordinary pattern of use must continue without effective interruption for the full limitations period and while the owner can legally object.",
      },
      {
        name: "Adverse use with owner knowledge",
        explanation:
          "The claimant's outward conduct must assert a right inconsistent with the owner's power to exclude and must overcome evidence or presumptions of permission.",
      },
    ],
    pitfalls: [
      "Friendly, unopposed driveway use may remain permissive for decades unless conduct clearly communicates a claim of right.",
      "Routine graveling or minor maintenance can be consistent with neighborly accommodation and may not, by itself, overcome the permissive-use presumption.",
      "Changes in route, meaningful interruptions, or gaps between predecessors can prevent a claimant from combining periods into one continuous ten-year use.",
      "The seven-year color-of-title and tax-payment statute concerns adverse possession and should not be treated as a shortcut for an ordinary prescriptive-easement claim.",
    ],
    workedExample: {
      title: "A shared driveway maintained for sixteen years",
      facts:
        "A homeowner has used a gravel driveway across the next lot for sixteen years and has occasionally graded and resurfaced it. Neither neighbor discussed permission until a dispute began.",
      analysis:
        "The route and duration satisfy important parts of Washington's test, but quiet use and ordinary maintenance may still look like tolerated neighborly conduct. Without an act that objectively asserted a right inconsistent with the owner's control, the claimant may not overcome the presumption of permission.",
      nextStep:
        "Review surveys, purchase documents, maintenance communications, prior-owner testimony, and any event that challenged or acknowledged the use, then consider negotiating a recorded easement instead of relying on silence.",
    },
    recordingGuidance:
      "A written easement should be recorded with the county auditor's recording division where the land lies. Washington's recording rules require attention to the first page or cover sheet, names, return address, parcel reference, and legal description. Verify the auditor's current fee and formatting instructions. A claimed prescriptive easement commonly becomes record notice through a recorded quiet-title judgment or later express agreement.",
    caseExample: {
      name: "Gamboa v. Clark",
      citation: "183 Wn.2d 38, 348 P.3d 1214 (2015)",
      facts:
        "Neighbors used and maintained a road across adjoining property for many years without an early dispute, then claimed a prescriptive easement after relations changed.",
      outcome:
        "The Washington Supreme Court applied a presumption of permissive use where the circumstances were consistent with neighborly accommodation and concluded that long use and maintenance, without enough contrary evidence, did not establish adversity.",
      lesson:
        "In Washington, a long period without objection can support permission rather than hostility; objective notice of an adverse claim is critical.",
      sourceUrl: "https://law.justia.com/cases/washington/supreme-court/2015/90239-0.html",
    },
    sources: [
      {
        label: "Washington ten-year real-property limitation",
        citation: "RCW 4.16.020(1)",
        url: "https://app.leg.wa.gov/RCW/default.aspx?cite=4.16.020",
      },
      {
        label: "Washington recording priority",
        citation: "RCW 65.08.070",
        url: "https://app.leg.wa.gov/RCW/default.aspx?cite=65.08.070",
      },
      {
        label: "Washington recording cover-sheet requirements",
        citation: "RCW 65.04.045",
        url: "https://app.leg.wa.gov/RCW/default.aspx?cite=65.04.045",
      },
    ],
  },
  {
    stateCode: "CA",
    distinctiveRule:
      "California uses a five-year prescriptive period and does not require an easement claimant to pay property taxes, unlike an adverse-possession claimant seeking title.",
    overview:
      "A California claimant generally must prove open, notorious, continuous, and adverse use under a claim of right for five years. The right is measured by the use actually made during that period. Property-tax payment belongs to an adverse-possession claim for ownership under Code of Civil Procedure section 325, not to a claim for a limited prescriptive easement.",
    burden:
      "The claimant must establish the elements by clear and convincing evidence. The owner's actual knowledge is not essential when the use is sufficiently open and notorious to provide constructive notice.",
    elements: [
      {
        name: "Open and notorious use",
        explanation:
          "The driveway, delivery path, parking pattern, or other use must be visible enough that a diligent owner would have notice of the claim.",
      },
      {
        name: "Continuous use for five years",
        explanation:
          "Continuity is judged according to the normal use of the claimed right; seasonal or periodic activity can qualify when that is how the easement would ordinarily be used.",
      },
      {
        name: "Adverse use under claim of right",
        explanation:
          "The claimant need not believe the use is legally authorized. The question is whether the use occurred without recognition of the owner's power to prevent it.",
      },
      {
        name: "Scope fixed by historic use",
        explanation:
          "A successful claim ordinarily covers the kind, intensity, and location of use proved during the five-year period, not every later expansion the claimant might prefer.",
      },
    ],
    pitfalls: [
      "Express or implied permission defeats adversity; a written license can clarify that use remains revocable.",
      "Paying property taxes is not required for a prescriptive easement, but it remains a statutory element of adverse possession under section 325.",
      "Civil Code section 813 offers a recorded-notice procedure aimed at preventing recreational users from acquiring rights, while Civil Code section 1008 addresses posted permission for public use; neither substitutes for advice on a private-neighbor dispute.",
      "A prescriptive use generally cannot mature against public property, and special statutory rules apply to coastal and public recreational access.",
    ],
    workedExample: {
      title: "Restaurant deliveries across a rear parking lot",
      facts:
        "Delivery trucks have crossed the same visible part of an adjoining parking lot to reach a restaurant's rear door for more than five years. No one asked the lot owner for permission.",
      analysis:
        "Regular deliveries over a definite route can provide notice and continuity even if the restaurant operator never believed it owned a legal right. Evidence of a license, interruptions, or a materially shifting path could change the result.",
      nextStep:
        "Preserve dated delivery records, tenant testimony, photographs, lot plans, and any owner communications that identify the route and whether use was permitted or challenged.",
    },
    recordingGuidance:
      "Record an agreed easement with the county recorder where the property is located, using a legal description and any necessary exhibit. California's base fee statutes interact with county and statewide surcharges, so use the recorder's current fee schedule rather than estimating from the base statute alone.",
    caseExample: {
      name: "Felgenhauer v. Soni",
      citation: "121 Cal. App. 4th 445 (2004)",
      facts:
        "For years, restaurant deliveries crossed a bank parking lot from an alley to the restaurant's rear door. The users had not asked the bank for permission, although one tenant testified that he did not believe he had a legal right to cross.",
      outcome:
        "The Court of Appeal affirmed the delivery easement. A claimant's mistaken belief about legal entitlement did not defeat claim of right because the use itself occurred without recognition of the owner's authority to prevent it.",
      lesson:
        "California tests the outward character of the use, not whether the user privately understood the law.",
      sourceUrl: "https://hallapproved.com/ca/cases/appeals/2004/2259491/",
    },
    sources: [
      {
        label: "California title by prescription",
        citation: "Cal. Civ. Code § 1007",
        url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=CIV&sectionNum=1007.",
      },
      {
        label: "California property-tax rule for adverse possession",
        citation: "Cal. Code Civ. Proc. § 325",
        url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=CCP&sectionNum=325.",
      },
      {
        label: "California recorded notice of consent",
        citation: "Cal. Civ. Code § 813",
        url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=CIV&sectionNum=813.",
      },
    ],
  },
  {
    stateCode: "MO",
    distinctiveRule:
      "Missouri requires ten years of visible, continuous, adverse use, but shared use does not automatically defeat a limited easement claim.",
    overview:
      "Missouri courts apply the ten-year real-property limitations period to prescriptive easements. The claimant must show use that was continuous, uninterrupted, visible, and adverse under a claim of right. Exclusivity in this setting means the right does not depend on a similar right in others; it does not always mean nobody else used the road or parking area.",
    burden:
      "The claimant must prove the elements by clear and convincing evidence. Once a qualifying long use is shown, Missouri cases may recognize presumptions, but evidence of permission or a use shared only as a neighborly accommodation can rebut adversity.",
    elements: [
      {
        name: "Visible use",
        explanation:
          "The owner must have actual or constructive notice through an observable road, parking pattern, gate, maintenance activity, or comparable conduct.",
      },
      {
        name: "Continuous and uninterrupted for ten years",
        explanation:
          "The relevant use must persist for the statutory period in the ordinary manner associated with the claimed right.",
      },
      {
        name: "Adverse use under claim of right",
        explanation:
          "The use must occur as an asserted right rather than at the owner's revocable invitation or indulgence.",
      },
      {
        name: "A limited right proved by its use",
        explanation:
          "The resulting easement is no broader than the route and purpose supported by the evidence, even when the owner or other neighbors also use the same area.",
      },
    ],
    pitfalls: [
      "Permission from a cemetery association, family member, prior owner, or neighbor can characterize decades of use as permissive rather than adverse.",
      "Shared use does not automatically destroy exclusivity, but use indistinguishable from a general privilege may fail to show an independent claim of right.",
      "Gaps, locked gates, route changes, or abandonment can undermine proof of one uninterrupted ten-year period.",
      "The ten-year limitations statute supplies the period; the detailed prescriptive-easement elements come from Missouri decisions, not from a standalone easement code section.",
    ],
    workedExample: {
      title: "A shared farm lane with an old permission",
      facts:
        "Two families have used the same farm lane for fifteen years. One family grades it each spring, but an earlier owner gave that family's predecessor oral permission to use the lane.",
      analysis:
        "Shared use is not necessarily fatal in Missouri, and visible grading may support notice. The old permission is more consequential: unless the use later became clearly adverse and remained so for ten years, the prescriptive clock may never have started.",
      nextStep:
        "Trace each ownership change and collect deeds, gate records, maintenance receipts, photographs, and testimony about when permission was given, withdrawn, or openly rejected.",
    },
    recordingGuidance:
      "A written easement should be recorded by the recorder of deeds in the county containing the property. Non-charter and charter counties may not use identical fee schedules, so verify the local recorder's current charges and formatting rules before submitting the signed instrument and legal description.",
    caseExample: {
      name: "Homan v. Hutchison",
      citation: "817 S.W.2d 944 (Mo. Ct. App. 1991)",
      facts:
        "Owners used a roadway leading toward a family cemetery and claimed a prescriptive road easement. The evidence included permission to use the road and other means of reaching their properties.",
      outcome:
        "The Missouri Court of Appeals affirmed denial of the prescriptive claim, accepting the trial court's conclusion that the use was permissive rather than adverse.",
      lesson:
        "A long-used road can remain a license when the evidence traces the use to permission instead of an asserted right.",
      sourceUrl: "https://law.justia.com/cases/missouri/court-of-appeals/1991/wd43830-2.html",
    },
    sources: [
      {
        label: "Missouri ten-year real-property limitation",
        citation: "RSMo § 516.010",
        url: "https://revisor.mo.gov/main/OneSection.aspx?section=516.010",
      },
      {
        label: "Missouri recording-fee authority",
        citation: "RSMo § 59.310",
        url: "https://revisor.mo.gov/main/OneSection.aspx?section=59.310",
      },
    ],
  },
  {
    stateCode: "OH",
    distinctiveRule:
      "Ohio requires twenty-one years and asks the claimant to prove each element by clear and convincing evidence, a higher standard than the ordinary civil default.",
    overview:
      "Ohio measures prescription against the twenty-one-year limitation in Ohio Rev. Code § 2305.04, which bars actions to recover title to or possession of real property after that period. Ohio decisions describe the prescriptive elements as open, notorious, adverse, continuous use for twenty-one years, and they treat the burden as demanding because the result takes a property right away from a record owner. Ohio also keeps prescription separate from easements implied from prior use and easements by necessity, which rest on how a single parcel was divided rather than on decades of use.",
    burden:
      "The claimant carries the burden on every element, and Ohio courts commonly describe that burden as clear and convincing evidence. Doubtful or ambiguous proof is generally resolved in favor of the record owner, so gaps in years, route, or adversity tend to defeat the claim rather than merely weaken it.",
    elements: [
      {
        name: "Twenty-one years of use",
        explanation:
          "The use must run for the full statutory period. Ohio's period is among the longest in the country, so old aerial photography, plats, and prior-owner testimony often matter more than recent activity.",
      },
      {
        name: "Open and notorious activity",
        explanation:
          "Driving, walking, parking, or piping must be visible enough that a reasonably attentive owner would learn of it without a survey or an investigation.",
      },
      {
        name: "Adverse use, not permission",
        explanation:
          "The use must be inconsistent with the owner's rights and made without the owner's consent. An owner's written or spoken permission at any point generally restarts the analysis.",
      },
      {
        name: "Continuous and uninterrupted use",
        explanation:
          "Use must follow a consistent pattern appropriate to its purpose. Seasonal farm or lake access can qualify if it recurs predictably, but an effective interruption by the owner breaks the chain.",
      },
    ],
    pitfalls: [
      "Clear and convincing evidence is a real hurdle: a neighbor's general recollection that the lane was 'always there' rarely fixes the start date twenty-one years back.",
      "Ohio does not let a claimant reach a prescriptive easement over property held by the state or a political subdivision for public use, so a claim across municipal land usually fails at the outset.",
      "A landowner who posts or delivers written notice, or who blocks the route, can interrupt the running period and force the clock to start over.",
      "Confusing prescription with an easement implied from prior use wastes the case; the implied theory depends on unified ownership followed by severance, not on counting years.",
    ],
    workedExample: {
      title: "A rural lane used since the 1990s",
      facts:
        "A homeowner in a township outside Columbus reaches a back field over a 12-foot gravel lane crossing the neighbor's parcel. The lane appears on aerial imagery from 1998, the family has graded it each spring, and no one ever discussed permission. The neighbor's new owner installs a gate.",
      analysis:
        "The imagery and maintenance history support open, notorious, and continuous use well beyond twenty-one years. The weak point is adversity: the claimant needs evidence that the use was never permitted, because Ohio's clear-and-convincing standard leaves little room for an ambiguous record. The gate itself does not erase a period that had already matured, but it does end any further accrual.",
      nextStep:
        "Assemble dated aerial photography, county auditor imagery, invoices for grading and gravel, and affidavits from prior owners on both sides, then compare that timeline with the deeds to see whether any recorded document already grants access.",
    },
    recordingGuidance:
      "Ohio easements are recorded with the county recorder where the land lies, and Ohio Rev. Code § 5301.25 makes an unrecorded interest ineffective against a later good-faith purchaser who records first. Many Ohio counties also require the county engineer's or auditor's transfer review before a document affecting a described strip can be recorded. Fees are set by statute and by local schedules, so confirm the current amount and the margin and font requirements with the recorder's office before submitting. A prescriptive right normally enters the record only through a recorded court judgment or a later written agreement between the owners.",
    caseExample: {
      name: "Shanks v. Floom",
      citation: "162 Ohio St. 479, 124 N.E.2d 416 (1955)",
      facts:
        "Adjoining owners in Ohio used a shared driveway between their homes for many years, and one of them later claimed a prescriptive easement over the strip.",
      outcome:
        "The Ohio Supreme Court held that use that is permissive in its inception does not become adverse merely because it continues for a long time, and that mutual, accommodating use between neighbors does not by itself establish the hostility prescription requires.",
      lesson:
        "In Ohio, decades of friendly shared use can still be legally permissive; the record must show when and how the use became a claim of right.",
      sourceUrl: "https://law.justia.com/cases/ohio/supreme-court-of-ohio/1955/34066-0.html",
    },
    sources: [
      {
        label: "Ohio twenty-one-year limitation on real-property actions",
        citation: "Ohio Rev. Code § 2305.04",
        url: "https://codes.ohio.gov/ohio-revised-code/section-2305.04",
      },
      {
        label: "Ohio recording and effect on subsequent purchasers",
        citation: "Ohio Rev. Code § 5301.25",
        url: "https://codes.ohio.gov/ohio-revised-code/section-5301.25",
      },
      {
        label: "Ohio county recorder fee schedule",
        citation: "Ohio Rev. Code § 317.32",
        url: "https://codes.ohio.gov/ohio-revised-code/section-317.32",
      },
    ],
  },
  {
    stateCode: "IN",
    distinctiveRule:
      "Indiana folds a statutory tax element into its adverse-use doctrine, so the question of who paid the taxes on the disputed strip can decide the case.",
    overview:
      "Indiana uses a twenty-year period and, in Fraley v. Minger, restated the common-law elements as control, intent, notice, and duration. Indiana also has a statute, Ind. Code § 32-21-7-1, requiring a claimant to have paid the taxes and special assessments that the claimant reasonably believed were due during the period. Indiana decisions apply the Fraley framework to prescriptive easements as well, while recognizing that the tax requirement fits awkwardly where a narrow easement strip is not separately assessed.",
    burden:
      "The claimant must establish each element by clear and convincing evidence. Because most driveways and paths carry no separate tax parcel, Indiana courts examine whether any distinct tax was actually assessed on the claimed interest before treating non-payment as fatal.",
    elements: [
      {
        name: "Control appropriate to the use",
        explanation:
          "The claimant must exercise the kind of control over the strip that a rightful holder of that easement would exercise, such as maintaining a lane rather than occasionally walking across a yard.",
      },
      {
        name: "Intent to claim the use as of right",
        explanation:
          "The claimant's conduct must show an intent to use the strip as a right against the world, including the record owner, rather than at the owner's sufferance.",
      },
      {
        name: "Notice to the owner",
        explanation:
          "The activity must be visible, open, and notorious enough that a reasonable owner would know a right was being asserted against the property.",
      },
      {
        name: "Twenty uninterrupted years",
        explanation:
          "Control, intent, and notice must coexist without interruption for the full twenty-year period; the years cannot be assembled from separate, unrelated stretches.",
      },
    ],
    pitfalls: [
      "The statutory tax element is unique enough that claimants overlook it; where the disputed interest was separately assessed and someone else paid, the claim is in serious trouble.",
      "Indiana treats permission as a complete answer, and a written license from a prior owner found in a closing file can end a twenty-year story immediately.",
      "Tacking requires privity between successive users; a buyer who simply started using the lane cannot borrow the seller's years without a connection in the chain of title.",
      "Occasional recreational crossing of open, unimproved ground usually falls short of the control Indiana requires, no matter how many years it continued.",
    ],
    workedExample: {
      title: "A shared farm drive north of Indianapolis",
      facts:
        "Two Hamilton County parcels share a single drive from the county road. The southern owner has used and plowed it since 1999, the strip is inside the northern owner's tax parcel, and no separate assessment has ever been issued for the drive itself.",
      analysis:
        "Twenty-plus years of plowing and daily use supports control, notice, and duration. Because the drive is not separately assessed, the tax statute is unlikely to defeat the claim on its own, but the intent element still turns on whether the original arrangement between the two families was a favor. A written note or an old handshake described in testimony can be decisive.",
      nextStep:
        "Pull the county assessor's parcel history to confirm no separate assessment exists, collect plowing and gravel receipts, and check both chains of title for a recorded or referenced access agreement.",
    },
    recordingGuidance:
      "Indiana easements are recorded with the county recorder in the county where the real estate is located. Indiana requires a prepared-by statement and a declaration that the preparer took reasonable care to redact Social Security numbers, and many counties require sales-disclosure or auditor endorsement steps for documents that transfer an interest. Recording fees are set by statute and updated periodically, so confirm the current schedule and formatting rules with the recorder before filing. A prescriptive easement becomes part of the record only through a quiet-title judgment or a later written grant.",
    caseExample: {
      name: "Fraley v. Minger",
      citation: "829 N.E.2d 476 (Ind. 2005)",
      facts:
        "A long-running dispute over rural Indiana land required the Indiana Supreme Court to restate what a claimant must show to acquire rights by adverse use and to address the statutory tax-payment requirement.",
      outcome:
        "The court reformulated the elements as control, intent, notice, and duration, and explained that the tax statute should be applied in light of whether taxes on the claimed interest were actually assessed, rather than as a mechanical bar in every case.",
      lesson:
        "Indiana claims are analyzed through the Fraley elements, and the tax question must be examined against the county's actual assessment records rather than assumed.",
      sourceUrl: "https://law.justia.com/cases/indiana/supreme-court/2005/06170501-shd.html",
    },
    sources: [
      {
        label: "Indiana property title code, including adverse-use provisions",
        citation: "Ind. Code Title 32",
        url: "https://iga.in.gov/laws/current/ic/titles/32",
      },
      {
        label: "Indiana adverse-possession tax-payment requirement",
        citation: "Ind. Code § 32-21-7-1",
        url: "https://iga.in.gov/laws/current/ic/titles/32#32-21-7-1",
      },
      {
        label: "Indiana recording requirements for conveyances",
        citation: "Ind. Code § 32-21-4-1",
        url: "https://iga.in.gov/laws/current/ic/titles/32#32-21-4-1",
      },
    ],
  },
  {
    stateCode: "PA",
    distinctiveRule:
      "Pennsylvania runs twenty-one years and presumes that use of unenclosed woodland was permissive, which quietly defeats many rural claims.",
    overview:
      "Pennsylvania grounds its twenty-one-year period in 42 Pa. Cons. Stat. § 5530 and requires adverse, open, notorious, continuous, and uninterrupted use for that period. Pennsylvania courts allow tacking of successive periods where the users are in privity, which matters in older boroughs where a lane may have served four or five owners. A distinctly Pennsylvania rule, sometimes traced to the state's timber history, presumes that passage over unenclosed woodland is permissive and therefore not adverse.",
    burden:
      "The claimant must prove each element. Where the land is unenclosed woodland, the claimant must also overcome the presumption of permissive use, which generally requires evidence of enclosure, improvement, or conduct that plainly asserted a right.",
    elements: [
      {
        name: "Twenty-one years of adverse use",
        explanation:
          "The period matches Pennsylvania's adverse-possession clock and runs only while the use is genuinely adverse to the owner's interest.",
      },
      {
        name: "Open, notorious, and visible use",
        explanation:
          "The route and pattern must be observable, such as a worn cartway, a paved apron, or a maintained farm lane, so the owner had a fair chance to object.",
      },
      {
        name: "Continuous and uninterrupted use",
        explanation:
          "Use must follow a regular pattern for the full period, and an effective physical or legal interruption by the owner resets the clock.",
      },
      {
        name: "Tacking through privity",
        explanation:
          "Successive owners may combine their periods to reach twenty-one years when each transfer passed along the use, which is often the only way an older lane qualifies.",
      },
    ],
    pitfalls: [
      "The unenclosed-woodland presumption is the most common surprise in Pennsylvania: crossing wooded acreage for decades may still be treated as permissive.",
      "Pennsylvania does not permit prescriptive rights against most public entities' land held for public use, so a claim across a township or authority parcel usually fails.",
      "A break in privity between successive owners prevents tacking, and a claimant who bought the property fifteen years ago cannot borrow the prior family's years without that connection.",
      "A recorded license or a written permission letter in the prior owner's file usually defeats adversity even when the physical use never changed.",
    ],
    workedExample: {
      title: "A borough alley used by two households",
      facts:
        "In a Westmoreland County borough, two households have used a paved alley across a third owner's lot to reach rear garages. Deeds going back to 1968 mention no easement, but each household's sellers described the alley to their buyers as part of the property's access.",
      analysis:
        "The alley is paved, enclosed by yards, and plainly visible, so the woodland presumption does not apply. The stronger question is tacking: the claimants need privity through each conveyance to reach twenty-one years. Because each seller passed along the use as part of the sale, tacking is plausible, but the closing files must confirm the chain rather than rely on memory.",
      nextStep:
        "Order the full deed chain for all three lots, review the borough's plan of lots for a dedicated alley, and preserve the sellers' statements about access before those recollections are lost.",
    },
    recordingGuidance:
      "Pennsylvania easements are recorded with the recorder of deeds in the county where the land sits. Pennsylvania requires a certificate of residence for the grantee and, in most counties, a uniform parcel identifier, and the recorder will reject documents with insufficient margins or missing acknowledgments. Fees combine a statutory base, county surcharges, and records-improvement charges, so confirm the current total with the recorder rather than relying on a published figure. A prescriptive easement enters the record only through a quiet-title decree or a later written grant.",
    caseExample: {
      name: "Waltimyer v. Smith",
      citation: "383 Pa. Super. 291, 556 A.2d 912 (1989)",
      facts:
        "A claimant argued that long use of a road over adjoining unenclosed, wooded land had ripened into a prescriptive easement.",
      outcome:
        "The Superior Court applied the rule that use of unenclosed woodland is presumed permissive and held that the claimant had not produced the evidence needed to convert that presumed permission into adverse use.",
      lesson:
        "In Pennsylvania, the character of the land matters as much as the years: wooded, unenclosed ground carries a presumption the claimant must affirmatively overcome.",
      sourceUrl: "https://law.justia.com/cases/pennsylvania/superior-court/1989/383-pa-super-291-1.html",
    },
    sources: [
      {
        label: "Pennsylvania twenty-one-year limitation for real property",
        citation: "42 Pa. Cons. Stat. § 5530",
        url: "https://www.legis.state.pa.us/cfdocs/legis/LI/consCheck.cfm?txtType=HTM&ttl=42&div=0&chpt=55",
      },
      {
        label: "Pennsylvania consolidated statutes index",
        citation: "Pennsylvania General Assembly, Consolidated Statutes",
        url: "https://www.legis.state.pa.us/cfdocs/legis/LI/Public/cons_index.cfm",
      },
    ],
  },
  {
    stateCode: "NY",
    distinctiveRule:
      "New York's ten-year period is one of the shortest in the country, and long open use creates a presumption of hostility that the owner must rebut.",
    overview:
      "New York measures prescription by the ten-year period in the Real Property Actions and Proceedings Law and requires use that is open, notorious, continuous, and hostile under a claim of right. New York's distinctive move is evidentiary: once a claimant shows open, notorious, continuous, and undisputed use for ten years, courts generally presume the use was hostile, and the burden shifts to the owner to show it was permissive. That presumption weakens or disappears where the parties are family members or where the parcels share a driveway in a way that suggests neighborly accommodation.",
    burden:
      "The claimant must establish the elements by clear and convincing evidence, but the presumption of hostility can carry the adversity element once the other facts are proved. An owner defeats the claim by producing credible evidence of permission or by showing the use was never exclusive of the owner's own rights in a meaningful way.",
    elements: [
      {
        name: "Ten years of use",
        explanation:
          "New York's short period means claims can mature within a single ownership, so recent history and current neighbors are often the key witnesses.",
      },
      {
        name: "Open and notorious use",
        explanation:
          "The use must be visible and obvious, such as a paved shared driveway, a fenced walkway, or a regularly driven service route.",
      },
      {
        name: "Continuous use without interruption",
        explanation:
          "The pattern must be consistent with the nature of the right claimed; a suburban driveway implies near-daily use, while a boat ramp may be seasonal.",
      },
      {
        name: "Hostility under a claim of right",
        explanation:
          "Use must be without the owner's permission. Proof of the other elements usually raises a presumption of hostility, which the owner then has the chance to rebut.",
      },
    ],
    pitfalls: [
      "The presumption of hostility does not apply between family members, so use across a relative's lot is generally treated as permissive.",
      "New York courts often treat shared-driveway use between cooperating neighbors as permissive accommodation, which cancels the usual presumption.",
      "A written or even credible oral permission at any point during the ten years defeats hostility and stops the clock.",
      "Ten years of use over land owned by the state, a city, or a public authority for public purposes will not create a prescriptive easement.",
    ],
    workedExample: {
      title: "A shared driveway on a Westchester street",
      facts:
        "Two 1940s homes sit close together, and the eastern owner has driven across a 4-foot sliver of the western lot to reach a detached garage since 2011. Both households repaved the shared apron together in 2016 and split the cost.",
      analysis:
        "Ten years of open, visible, daily use is established and would normally raise the presumption of hostility. The shared repaving cuts the other way: cooperative, mutual maintenance is exactly the pattern New York courts read as neighborly accommodation rather than an adverse claim, which may rebut the presumption.",
      nextStep:
        "Locate the 2016 payment records and any messages about the repaving, obtain a current survey showing the encroaching travel path, and weigh negotiating a recorded mutual driveway easement, which is common and inexpensive compared with litigation.",
    },
    recordingGuidance:
      "New York easements are recorded with the county clerk, except in New York City, where recording runs through the City Register for the Bronx, Brooklyn, Manhattan, and Queens, while Staten Island records with the Richmond County Clerk. Under Real Property Law § 291, an unrecorded conveyance is void against a later good-faith purchaser who records first. New York City filings go through the ACRIS system and require transfer-tax returns even for a no-consideration easement. Confirm current recording and tax-form requirements with the clerk or register before filing, because the schedules change.",
    caseExample: {
      name: "Di Leo v. Pecksto Holding Corp.",
      citation: "304 N.Y. 505, 109 N.E.2d 600 (1952)",
      facts:
        "Claimants used a roadway across neighboring land in Westchester County over a long period and sought recognition of a prescriptive easement.",
      outcome:
        "The Court of Appeals confirmed that where use is shown to be open, notorious, continuous, and undisputed for the prescriptive period, a presumption arises that the use was hostile and under a claim of right, placing the burden on the owner to show permission.",
      lesson:
        "In New York, proving the visible facts of long use can shift the decisive adversity question onto the landowner.",
      sourceUrl: "https://law.justia.com/cases/new-york/court-of-appeals/1952/304-n-y-505-0.html",
    },
    sources: [
      {
        label: "New York adverse-possession and prescription article",
        citation: "N.Y. Real Prop. Acts. Law § 501",
        url: "https://www.nysenate.gov/legislation/laws/RPA/501",
      },
      {
        label: "New York adverse-possession period and claim of right",
        citation: "N.Y. Real Prop. Acts. Law § 511",
        url: "https://www.nysenate.gov/legislation/laws/RPA/511",
      },
      {
        label: "New York recording act",
        citation: "N.Y. Real Prop. Law § 291",
        url: "https://www.nysenate.gov/legislation/laws/RPP/291",
      },
    ],
  },
  {
    stateCode: "NC",
    distinctiveRule:
      "North Carolina presumes that use of another's land is permissive, so a claimant must affirmatively prove hostility for the full twenty years.",
    overview:
      "North Carolina requires twenty years of adverse use for a prescriptive easement under N.C. Gen. Stat. § 1-40, while adverse possession under color of title can mature in seven years under § 1-38. The two should not be confused: color of title is a shortcut to ownership backed by a defective writing, not a shortcut to an easement. North Carolina's defining rule is a strong presumption that the use of a way over another's land is permissive, and the claimant must rebut it with evidence that the use was adverse and under a claim of right.",
    burden:
      "The claimant must prove each element by the greater weight of the evidence and must overcome the presumption of permission. North Carolina also requires that the claimed way be substantially the same route throughout the twenty years, which defeats claims over paths that wandered as fields or tree lines changed.",
    elements: [
      {
        name: "Adverse or hostile use",
        explanation:
          "The use must be made under a claim of right known to the owner, and the claimant must overcome the presumption that it began as a neighborly permission.",
      },
      {
        name: "Open and notorious use",
        explanation:
          "The use must be obvious enough that the owner had notice and a fair opportunity to object during the twenty-year period.",
      },
      {
        name: "Continuous and uninterrupted use for twenty years",
        explanation:
          "The pattern must persist for the full period; an owner who blocks the way or formally objects can interrupt the running of the clock.",
      },
      {
        name: "A substantially identical route",
        explanation:
          "The claimed way must stay in substantially the same location across the whole period, which is why shifting field roads and rerouted logging trails often fail.",
      },
    ],
    pitfalls: [
      "The presumption of permission is the central obstacle: unlike states where long use implies hostility, North Carolina starts from the opposite assumption.",
      "A route that shifted position over the years breaks the substantially-identical-route requirement even when total use easily exceeded twenty years.",
      "Confusing the seven-year color-of-title statute with prescription leads claimants to argue the wrong theory; color of title concerns possession of land under a defective writing, not an easement.",
      "Under the Torrens registration system still available in a few North Carolina counties, registered land is generally protected from prescriptive claims.",
    ],
    workedExample: {
      title: "A logging road in a coastal-plain county",
      facts:
        "A family reaches timberland over a dirt road crossing a neighbor's tract. Aerial imagery from 1996 shows a road in roughly the area, but the 2008 and 2019 images show the track about 120 feet farther north after a ditch was dug.",
      analysis:
        "Total use may exceed twenty years, but the relocation is a serious problem in North Carolina because the way must remain substantially the same. The claimant would also need affirmative evidence of adversity to rebut the presumption that the original crossing was allowed by a cooperative neighbor.",
      nextStep:
        "Georeference the aerial series to measure how far the track actually moved, look for any recorded timber deed or access agreement, and consider negotiating a written easement along the current alignment.",
    },
    recordingGuidance:
      "North Carolina easements are recorded with the register of deeds in the county where the land lies, and N.C. Gen. Stat. § 47-18 makes an unrecorded interest ineffective against a later purchaser for value who records first. The register will check margins, legible text, a proper notarial acknowledgment, and, in most counties, a first-page return address and brief description. Fees follow a statutory base with page charges, so verify the current schedule with the register before filing. A prescriptive easement enters the chain of title only through a recorded judgment or a later written grant.",
    caseExample: {
      name: "Dickinson v. Pake",
      citation: "284 N.C. 576, 201 S.E.2d 897 (1974)",
      facts:
        "Owners claimed a right of way over a neighboring tract based on many years of use of a roadway in a coastal North Carolina community.",
      outcome:
        "The North Carolina Supreme Court restated that the law presumes the use of a way over another's land is permissive, and that the claimant bears the burden of rebutting that presumption with evidence of adverse use under a claim of right for twenty years over substantially the same route.",
      lesson:
        "North Carolina claimants must build affirmative proof of hostility and a stable route, because time alone works against them.",
      sourceUrl: "https://law.justia.com/cases/north-carolina/supreme-court/1974/23-2.html",
    },
    sources: [
      {
        label: "North Carolina twenty-year adverse-use period",
        citation: "N.C. Gen. Stat. § 1-40",
        url: "https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_1/GS_1-40.html",
      },
      {
        label: "North Carolina seven-year color-of-title statute",
        citation: "N.C. Gen. Stat. § 1-38",
        url: "https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_1/GS_1-38.html",
      },
      {
        label: "North Carolina recording act",
        citation: "N.C. Gen. Stat. § 47-18",
        url: "https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_47/GS_47-18.html",
      },
    ],
  },
];

/**
 * Every state profile, region by region. The core list was written first and
 * the regional files complete the map; all of them share one interface so the
 * state pages render them identically.
 */
export const ENHANCED_STATE_LAW_PROFILES: EnhancedStateLawProfile[] = [
  ...CORE_STATE_LAW_PROFILES,
  ...SOUTH_STATE_LAW_PROFILES,
  ...NORTHEAST_STATE_LAW_PROFILES,
  ...MIDWEST_STATE_LAW_PROFILES,
  ...WEST_STATE_LAW_PROFILES,
].sort((a, b) => a.stateCode.localeCompare(b.stateCode));

const PROFILES_BY_STATE = new Map<string, EnhancedStateLawProfile>(
  ENHANCED_STATE_LAW_PROFILES.map((profile) => [profile.stateCode, profile]),
);

export function enhancedStateLawProfileFor(
  stateCode: string,
): EnhancedStateLawProfile | null {
  return PROFILES_BY_STATE.get(stateCode.toUpperCase()) ?? null;
}